/**
 * Utility for managing tracking parameters across navigation
 */

// Common tracking parameters that should be preserved
const TRACKING_PARAMS = [
  '_gl', // Google Ads auto-tagging parameter
  '_ga', // Google Analytics cross-domain measurement
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid', // Google Click ID
  'fbclid', // Facebook Click ID
  'msclkid', // Microsoft Click ID
];

export class URLTrackingManager {
  private trackingParams: URLSearchParams;

  constructor() {
    this.trackingParams = new URLSearchParams();
    this.extractTrackingParams();
  }

  /**
   * Extract tracking parameters from current URL
   */
  private extractTrackingParams(): void {
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);
    
    TRACKING_PARAMS.forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        this.trackingParams.set(param, value);
      }
    });
  }

  /**
   * Get all tracking parameters as an object
   */
  getTrackingParams(): Record<string, string> {
    const params: Record<string, string> = {};
    this.trackingParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }

  /**
   * Get tracking parameters as URL search string
   */
  getTrackingParamsString(): string {
    return this.trackingParams.toString();
  }

  /**
   * Add tracking parameters to a URL
   */
  addTrackingParamsToUrl(url: string): string {
    if (this.trackingParams.toString() === '') {
      return url;
    }

    try {
      const urlObj = new URL(url, window.location.origin);
      
      // Add each tracking param to the URL
      this.trackingParams.forEach((value, key) => {
        urlObj.searchParams.set(key, value);
      });
      
      return urlObj.toString();
    } catch {
      // If URL parsing fails, try simple concatenation
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}${this.trackingParams.toString()}`;
    }
  }

  /**
   * Merge additional parameters
   */
  addParams(params: Record<string, string>): void {
    Object.entries(params).forEach(([key, value]) => {
      if (TRACKING_PARAMS.includes(key)) {
        this.trackingParams.set(key, value);
      }
    });
  }

  /**
   * Clear all tracking parameters
   */
  clear(): void {
    this.trackingParams = new URLSearchParams();
  }

  /**
   * Create a navigation handler that preserves tracking params
   */
  createNavigationHandler(navigate: (path: string) => void) {
    return (path: string) => {
      const enhancedPath = this.addTrackingParamsToUrl(path);
      navigate(enhancedPath);
    };
  }

  /**
   * Check if URL has any tracking parameters
   */
  hasTrackingParams(): boolean {
    return this.trackingParams.toString() !== '';
  }

  /**
   * Get specific tracking parameter
   */
  getParam(key: string): string | null {
    return this.trackingParams.get(key);
  }
}

// Export singleton instance
export const urlTrackingManager = new URLTrackingManager();

// Helper functions
export const getTrackingParams = () => urlTrackingManager.getTrackingParams();
export const addTrackingParamsToUrl = (url: string) => urlTrackingManager.addTrackingParamsToUrl(url);
export const hasTrackingParams = () => urlTrackingManager.hasTrackingParams();
export const getTrackingParam = (key: string) => urlTrackingManager.getParam(key);